import React, { Fragment } from 'react';
import { G } from '@react-pdf/renderer';
import Icons from '../../globalConstants/Icons/Report';
import { isEmpty, keys } from 'lodash';

/**
 * Interface for loading docks configuration
 */
interface LoadingDocksConfig {
  left_docks: number;
  right_docks: number;
  top_docks: number;
  bottom_docks: number;
  [key: string]: number | boolean;
}

/**
 * Interface for area dimensions
 */
interface AreaDimensions {
  length: number;
  width: number;
  [key: string]: number;
}

/**
 * Interface for area
 */
interface Area {
  id: string;
  dimensions?: AreaDimensions;
  [key: string]: any;
}

/**
 * Interface for areas object
 */
interface Areas {
  [key: string]: Area;
}

/**
 * Interface for loading dock position
 */
interface DockPosition {
  x: number;
  y: number;
  rotate: number;
}

/**
 * Interface for constants
 */
interface Constants {
  container?: {
    baySize: number;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Interface for bounding box
 */
interface BoundingBox {
  width: number;
  height: number;
}

/**
 * Interface for content
 */
interface Content {
  loadingDocks: LoadingDocksConfig;
  ghostDocks: string[];
  areas: Areas;
  [key: string]: any;
}

/**
 * Interface for component props
 */
interface MuiLoadingDocksProps {
  inContainer?: boolean;
  areaId?: string;
  bbox: BoundingBox;
  scale: number;
  content: Content;
  constants: Constants;
}

/**
 * MUI LoadingDocks component that replaces the styled-components LoadingDocks component
 * 
 * This component provides a Material UI equivalent of the original LoadingDocks component
 * with similar props and functionality. It's used for PDF generation.
 * 
 * @param {MuiLoadingDocksProps} props
 * @returns {JSX.Element} MUI LoadingDocks component
 */
const MuiLoadingDocks: React.FC<MuiLoadingDocksProps> = (props) => {
  const { inContainer, areaId, bbox, scale, content: { loadingDocks, ghostDocks, areas }, constants } = props;
  const area = areaId ? areas?.[areaId] : {} as Area;
  let docks: DockPosition[] = [];
  const bay = (constants.container?.baySize ?? 0) * scale;
  
  if (!isEmpty(loadingDocks)) {
    // Horizontal calculation
    let qty = (bbox.width - 2) / bay;
    const hqty = (qty % 1 === 0 ? qty - 1 : parseInt(String(qty))) - (loadingDocks.left_docks + loadingDocks.right_docks);
    const hstart = (bbox.width - 2) / 2 - (hqty + loadingDocks.right_docks - loadingDocks.left_docks) * bay / 2;
    const horizontal = Array.from({ length: hqty }, (_, index) => hstart + index * bay);
    
    // Vertical calculation
    qty = (inContainer ? bbox.height - 4 : (area?.dimensions?.width ?? 0) * scale) / bay;
    const vqty = (qty % 1 === 0 ? qty - 1 : parseInt(String(qty))) - (loadingDocks.top_docks + loadingDocks.bottom_docks);
    const vstart = (inContainer ? bbox.height - 4 : (area?.dimensions?.width ?? 0) * scale) / 2 - (vqty + loadingDocks.bottom_docks - loadingDocks.top_docks) * bay / 2;
    const vertical = Array.from({ length: vqty }, (_, index) => vstart + index * bay);

    // Merge docks
    const sides = inContainer 
      ? loadingDocks 
      : keys(loadingDocks).reduce<Record<string, boolean | number>>((acc, key) => {
          return { ...acc, [key]: area?.id?.includes(key) };
        }, {});
        
    docks = vertical.flatMap((pos, index) => {
      return [
        ...sides.left_docks && !ghostDocks.includes(`left-${index + 1}`) ? [{ x: 1, y: pos + 1, rotate: 90 }] : [],
        ...sides.right_docks && !ghostDocks.includes(`right-${index + 1}`) ? [{ x: (inContainer ? bbox.width - 2 : (area?.dimensions?.length ?? 0) * scale), y: pos + 1, rotate: -90 }] : []
      ]
    }).concat(horizontal.flatMap((pos, index) => {
      return [
        ...sides.top_docks && !ghostDocks.includes(`top-${index + 1}`) ? [{ x: pos + 1, y: 1, rotate: 180 }] : [],
        ...sides.bottom_docks && !ghostDocks.includes(`bottom-${index + 1}`) ? [{ x: pos + 1, y: (inContainer ? bbox.height - 4 : (area?.dimensions?.width ?? 0) * scale), rotate: 0 }] : []
      ]
    }));
  }
  
  return (
    <Fragment>
      {docks.map((dock, index) =>
        <G key={`dock-${index}`} transform={`translate(${dock.x} ${dock.y}) rotate(${dock.rotate}) translate(-${[-90, 180].includes(dock.rotate) ? bay : 0} 0) scale(${scale / 10})`} >
          <Icons.LoadingDock />
        </G>
      )}
    </Fragment>
  );
};

export default MuiLoadingDocks;
